import moment from "moment";

/**
 * @description Maps the backend Order EDD API response into the UI view model
 * @param {Object} apiData Raw backend API response from /joms/api/external/v1/order/edd/:orderNumber
 * @param {Object} orderDetails Redux orderDetails state object
 * @returns {Object} Mapped data for DeliveryDateTab components
 */
export const mapOrderEddResponse = (apiData, orderDetails = {}) => {
  if (!apiData) return null;

  const eddAvailable = apiData.eddAvailable ?? true;
  const eddResponse = apiData.eddResponse?.data || apiData.data || apiData;

  const aggregateEdd = eddResponse?.aggregate_edd;
  const calculationTime = eddResponse?.calculation_time;

  const committedDeliveryDate = aggregateEdd
    ? moment(aggregateEdd).format("DD MMM YYYY")
    : "-";

  const frozenAt = calculationTime
    ? moment(calculationTime).format("DD MMM YYYY hh:mm:ss A")
    : "-";

  const shippingAddress = orderDetails?.buyerDetails?.shippingAddress;
  const destination = shippingAddress
    ? [shippingAddress.postalCode, shippingAddress.city || shippingAddress.state]
        .filter(Boolean)
        .join(" · ")
    : "-";

  const reservation =
    orderDetails?.reservationId ||
    orderDetails?.reservationNumber ||
    eddResponse?.reference_id ||
    "-";

  const rawItems = Array.isArray(eddResponse?.items) ? eddResponse.items : [];
  const lineItems = Array.isArray(orderDetails?.lineItemDetails)
    ? orderDetails.lineItemDetails
    : [];

  const items = rawItems.map((item, index) => {
    const matchingLineItem =
      lineItems.find(
        (li) =>
          li.lineItemId === item.reference_id ||
          item.reference_id?.includes(li.lineItemId) ||
          li.reference_id === item.reference_id
      ) || lineItems[index];

    const productName =
      matchingLineItem?.productInfo?.name ||
      `Item ${index + 1} (${item.reference_id || item.mmid || "SKU"})`;

    const subtitle =
      matchingLineItem?.sourceSeller?.sellerName ||
      item.vendor_location_key ||
      "-";

    const qty = item.qty
      ? `${item.qty} MT`
      : matchingLineItem?.quantityOrdered
      ? `${matchingLineItem.quantityOrdered} ${matchingLineItem.uom || "MT"}`
      : "-";

    const breakup = item.sla_breakup || {};
    const materialReady = `${breakup.material_readiness?.duration_hours ?? 0} h`;
    const serviceCentre = `${breakup.service_centre?.duration_hours ?? 0} h`;
    const processing = `${breakup.order_processing?.duration_hours ?? 0} h`;
    const logistics = `${breakup.logistics?.duration_hours ?? 0} h`;
    const nonWorking = `${breakup.buffer?.duration_hours ?? 0} h`;

    const committedDate = item.edd ? moment(item.edd).format("DD MMM YYYY") : "-";
    const isOrderDateSetter = Boolean(
      item.edd && aggregateEdd && item.edd === aggregateEdd
    );

    return {
      id: item.reference_id || String(index + 1),
      name: productName,
      subtitle,
      quantity: qty,
      materialReady,
      serviceCentre,
      processing,
      logistics,
      nonWorking,
      committedDate,
      isOrderDateSetter,
      tag: isOrderDateSetter ? "Sets order date" : undefined,
    };
  });

  return {
    eddAvailable,
    header: {
      committedDeliveryDate,
      frozenAt,
      destination,
      reservation,
    },
    items,
    processingFormulaNote:
      "Processing includes payment verification, DO release, JOTS vehicle reporting, and dispatch SLAs. Buffer hours are included in committed dates.",
  };
};
